/**
 * Estrazione dei dati aziendali dalla firma di una email.
 * Modulo puro: nessuna chiamata di rete, nessuna dipendenza dallo store.
 *
 * Il testo citato viene tagliato prima del parsing: nelle catene di risposta
 * compaiono firme di altri interlocutori, e pescare i loro dati significherebbe
 * creare un'azienda sbagliata.
 */

export interface ParsedSignature {
  websites: string[];
  socialLinks: string[];
  phones: string[];
  /** Numeri fissi (prefisso 0): sono il recapito dell'azienda. */
  landlines: string[];
  /** Numeri mobili (prefisso 3): sono il recapito della persona. */
  mobiles: string[];
  vatNumber: string | null;
  taxCode: string | null;
  role: string | null;
  address: string | null;
}

const SOCIAL_HOSTS = [
  'facebook.com', 'fb.com', 'instagram.com', 'linkedin.com', 'twitter.com', 'x.com',
  'youtube.com', 'youtu.be', 'tiktok.com', 'pinterest.com', 'wa.me', 'whatsapp.com',
  't.me', 'telegram.me', 'maps.google.com', 'goo.gl', 'g.page',
];

// Host di tracciamento e infrastruttura: non sono mai il sito del cliente.
const NOISE_HOSTS = [
  'googleusercontent.com', 'gstatic.com', 'google.com', 'schema.org', 'w3.org',
  'mailchimp.com', 'list-manage.com', 'sendgrid.net', 'mailjet.com', 'doubleclick.net',
  'flusia.it', 'obly.it',
];

const QUOTE_MARKERS = [
  /Il giorno\s+[\s\S]{0,120}?ha scritto:/i,
  /-{2,}\s*Original Message\s*-{2,}/i,
  /-{2,}\s*Messaggio originale\s*-{2,}/i,
  /^\s*Da:\s.+$/im,
  /On\s+[\s\S]{0,120}?wrote:/i,
];

const ROLE_KEYWORDS = [
  'amministratore', 'titolare', 'responsabile', 'direttore', 'direttrice', 'presidente',
  'socio', 'contitolare', 'ceo', 'cfo', 'cto', 'coo', 'general manager', 'sales manager',
  'account', 'commerciale', 'ufficio tecnico', 'ufficio acquisti', 'marketing manager',
  'legale rappresentante', 'procuratore', 'consulente', 'geometra', 'ingegnere', 'architetto',
];

/** Rimuove i blockquote e tutto quanto segue un marcatore di citazione. */
export function stripQuotedText(html: string): string {
  let out = html.replace(/<blockquote[\s\S]*?<\/blockquote>/gi, ' ');
  out = out.replace(/<div[^>]*class="[^"]*gmail_quote[^"]*"[\s\S]*$/i, ' ');
  for (const marker of QUOTE_MARKERS) {
    const m = out.match(marker);
    if (m && m.index !== undefined) out = out.slice(0, m.index);
  }
  return out;
}

function htmlToText(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/[ \t]+/g, ' ');
}

function normalizeHost(raw: string): string | null {
  let host = raw.trim().toLowerCase();
  host = host.replace(/^https?:\/\//, '').replace(/^www\./, '');
  host = host.split('/')[0].split('?')[0].split('#')[0];
  if (!host.includes('.')) return null;
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host)) return null;
  return host;
}

function collectUrls(html: string, text: string): string[] {
  const found: string[] = [];
  const hrefRe = /href\s*=\s*["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = hrefRe.exec(html)) !== null) {
    if (/^(mailto|tel):/i.test(m[1])) continue;
    found.push(m[1]);
  }
  const bareRe = /(?:https?:\/\/|www\.)[a-z0-9.-]+\.[a-z]{2,}(?:\/[^\s<>"']*)?/gi;
  const bare = text.match(bareRe) || [];
  return [...found, ...bare];
}

/**
 * Normalizza un numero italiano in formato +39XXXXXXXXX. Null se implausibile.
 * La soglia minima e` 8 cifre perche' molti centralini italiani sono corti
 * (es. 0571 4441): scartarli lascerebbe l'azienda senza telefono.
 */
function normalizePhone(raw: string): string | null {
  let n = raw.replace(/[^\d+]/g, '');
  if (n.startsWith('00')) n = '+' + n.slice(2);
  if (!n.startsWith('+')) {
    if (n.length < 8 || n.length > 11) return null;
    n = '+39' + n;
  }
  const digits = n.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return null;
  return n;
}

/** Distingue fisso da mobile su numerazione italiana: i mobili iniziano per 3. */
function isItalianMobile(normalized: string): boolean {
  const it = normalized.startsWith('+39') ? normalized.slice(3) : null;
  return !!it && it.startsWith('3');
}

export function parseSignature(input: { bodyHtml?: string | null; bodyText?: string | null }): ParsedSignature {
  const result: ParsedSignature = {
    websites: [], socialLinks: [], phones: [], landlines: [], mobiles: [],
    vatNumber: null, taxCode: null, role: null, address: null,
  };

  const rawHtml = input.bodyHtml || '';
  const html = rawHtml ? stripQuotedText(rawHtml) : '';
  const text = html ? htmlToText(html) : stripQuotedText(input.bodyText || '');

  if (!text.trim() && !html.trim()) return result;

  // Siti e social
  const seenSites = new Set<string>();
  for (const url of collectUrls(html, text)) {
    const host = normalizeHost(url);
    if (!host) continue;
    if (NOISE_HOSTS.some(n => host === n || host.endsWith('.' + n))) continue;
    if (SOCIAL_HOSTS.some(s => host === s || host.endsWith('.' + s))) {
      if (!result.socialLinks.includes(url)) result.socialLinks.push(url);
      continue;
    }
    if (!seenSites.has(host)) {
      seenSites.add(host);
      result.websites.push(host);
    }
  }

  // Partita IVA: serve un'etichetta esplicita, altrimenti un telefono verrebbe scambiato per P.IVA
  const vatMatch = text.match(/(?:p\.?\s*iva|partita\s+iva|vat(?:\s+number)?|c\.f\.\s*\/\s*p\.\s*iva)[^0-9a-z]{0,10}((?:IT)?\d{11})/i);
  if (vatMatch) result.vatNumber = vatMatch[1].replace(/^IT/i, '');

  const cfMatch = text.match(/(?:cod(?:ice)?\.?\s*fisc(?:ale)?|c\.f\.)[^0-9a-z]{0,10}([A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z])/i);
  if (cfMatch) result.taxCode = cfMatch[1].toUpperCase();

  // Telefoni: solo quelli con etichetta, per non pescare numeri civici o codici
  const phoneRe = /(?:tel|telefono|cell|cellulare|mobile|mob|phone|fax|t\.|m\.)[^0-9+]{0,6}((?:\+?\d[\d\s().-]{7,})\d)/gi;
  let pm: RegExpExecArray | null;
  while ((pm = phoneRe.exec(text)) !== null) {
    const norm = normalizePhone(pm[1]);
    if (norm && !result.phones.includes(norm)) {
      result.phones.push(norm);
      if (isItalianMobile(norm)) result.mobiles.push(norm);
      else result.landlines.push(norm);
    }
  }

  // Qualifica: prima riga breve che contiene una parola chiave di ruolo
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  for (const line of lines) {
    if (line.length > 60) continue;
    const lower = line.toLowerCase();
    if (ROLE_KEYWORDS.some(k => lower.includes(k))) { result.role = line; break; }
  }

  // Indirizzo: riga con CAP a cinque cifre
  const addrLine = lines.find(l => /\b\d{5}\b/.test(l) && l.length < 120 && !/iva|fisc/i.test(l));
  if (addrLine) result.address = addrLine;

  return result;
}
